import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  brandColours,
  breakpointSizes,
  fluidFontSize,
  fontSize,
  fontWeights,
  maxBreakpointQuery,
  minBreakpointQuery,
  standardColours,
  standardTransition,
  visuallyHidden,
  zIndexLayers,
} from '../styles';
import { Link, Button } from './ui';

const StyledNavigation = styled.nav`
  ${({ isSearchOpen }) => {
    if (isSearchOpen) {
      return css`
        ${maxBreakpointQuery.large`
          display:none;
        `}
      `;
    }
  }}
`;

const StyledToggle = styled.button`
  ${maxBreakpointQuery.large`
    position: relative;
    margin: 0;
    padding: 2px;
    height: 100%;
    background: none;
    border: none;
  `}

  ${minBreakpointQuery.large`
    display: none;
  `}

  span {
    ${maxBreakpointQuery.large`
      display: block;
      margin-top: 6px;
      margin-bottom: 6px;
      height: 2px;
      width: 24px;
      background-color: ${standardColours.white};
      transition: ${standardTransition('opacity')},
        ${standardTransition('transform')};
    `}

    ${({ displayNav }) => {
      if (displayNav) {
        return css`
          ${maxBreakpointQuery.large`
            opacity: 0;
          `}

          &:first-child {
            ${maxBreakpointQuery.large`
              opacity: 1;
              transform: translateY(8px) rotate(45deg);
            `}
          }

          &:last-child {
            ${maxBreakpointQuery.large`
              opacity: 1;
              transform: translateY(-8px) rotate(-45deg);
            `}
          }
        `;
      }
    }}
  }
`;

const StyledInner = styled.div`
  ${maxBreakpointQuery.large`
    position: absolute;
    right: 0;
    left: 0;
    z-index: ${zIndexLayers.fourth};
    display: ${({ displayNav }) => (displayNav ? 'block' : 'none')};
    padding: 40px 30px;
    height: 100%;
    background-color: ${brandColours.quaternary};
  `}
`;

const StyledList = styled.ul`
  ${maxBreakpointQuery.large`
    margin-bottom: 20px;
    text-align: center;
  `}

  ${minBreakpointQuery.large`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px 20px;
  `}

  ${minBreakpointQuery.xlarge`
    column-gap: 30px;
  `}

  ${minBreakpointQuery.xxlarge`
    column-gap: 40px;
  `}

  ${minBreakpointQuery.xxxlarge`
    column-gap: 50px;
  `}
`;

// moved up for hover
const StyledLink = styled(Link)`
  display: block;
  padding: 4px 0;
  color: ${standardColours.white};
  font-weight: ${fontWeights.medium};
  transition: ${standardTransition('color')};

  ${maxBreakpointQuery.large`
    ${fluidFontSize(
      '18px',
      '26px',
      breakpointSizes.tiny,
      breakpointSizes.large,
    )};
  `}

  ${minBreakpointQuery.large`
    padding: 14px 6px;
    text-align: center;
    ${fontSize(14)};
  `}

  ${minBreakpointQuery.xlarge`
    ${fontSize(16)};
  `}

  &:hover,
  &.current-page {
    ${minBreakpointQuery.large`
      color: ${brandColours.tertiary};
    `}
  }

  ${({ hasChildren }) => {
    if (hasChildren) {
      return css`
        ${minBreakpointQuery.large`
          position: relative;
          padding-right: 22px;
        `}

        &:before,
        &:after {
          ${minBreakpointQuery.large`
            content: '';
            position: absolute;
            display: block;
            height: 8px;
            width: 8px;
            border-radius: 1px;
          `}
        }

        &:before {
          ${minBreakpointQuery.large`
            top: 50%;
            right: 1px;
            border: 2px solid ${standardColours.white};
            border-top: none;
            border-left: none;
            transform: translateY(-50%) rotate(45deg);
            transition: ${standardTransition('border-color')};
          `}
        }

        &:after {
          ${minBreakpointQuery.large`
            top: 100%;
            left: 50%;
            background-color: ${standardColours.white};
            opacity: 0;
            transform: translate(-50%, -50%) rotate(45deg);
          `}
        }

        &:hover,
        &.current-page {
          &:before {
            ${minBreakpointQuery.large`
              border-color: ${brandColours.tertiary};
            `}
          }
        }
      `;
    }
  }}
`;

// moved up for hover
const StyledSubList = styled.ul`
  ${minBreakpointQuery.large`
    position: absolute;
    left: -9999em;
    z-index: ${zIndexLayers.fourth};
    padding: 16px;
    width: 180px;
    background-color: ${standardColours.white};
    border-radius: 10px;
    box-shadow: 0 2px 3px ${standardColours.grey};
    transform: translateX(-50%);
  `}

  ${minBreakpointQuery.xlarge`
    padding: 20px
  `}
`;

const StyledItem = styled.li`
  ${maxBreakpointQuery.large`
    margin-top: 20px;
  `}

  ${minBreakpointQuery.large`
    position: relative;
    display: inline-block;
  `}

  &:first-child {
    ${maxBreakpointQuery.large`
      margin-top: 0;
    `}

    ${minBreakpointQuery.large`
      margin-left: 0;
    `}
  }

  &:hover {
    ${StyledSubList} {
      ${minBreakpointQuery.large`
        left: 50%;
      `}
    }

    ${({ hasChildren }) => {
      if (hasChildren) {
        return css`
          ${StyledLink} {
            &:after {
              ${minBreakpointQuery.large`
                opacity: 1;
              `}
            }
          }
        `;
      }
    }}
  }
`;

const StyledSubItem = styled.li`
  margin-top: 8px;

  &:first-child {
    ${minBreakpointQuery.large`
      margin-top: 0;
    `}
  }
`;

const StyledSubLink = styled(Link)`
  display: block;
  padding: 4px 0;
  ${fontSize(15)};
  transition: ${standardTransition('color')};

  ${maxBreakpointQuery.large`
    color: ${standardColours.white};
  `}

  ${minBreakpointQuery.smedium`
    ${fontSize(16)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(14)};
    text-align: center;
  `}

  &:hover,
  &.current-page {
    ${minBreakpointQuery.large`
      color: ${brandColours.secondary};
    `}
  }
`;

const commonButtonStyles = () => {
  return css`
    ${maxBreakpointQuery.large`
      margin-top: 20px;
      padding-top: 14px;
      padding-bottom: 14px;
      width: 100%;
    `}

    ${minBreakpointQuery.large`
      ${visuallyHidden()};
    `}
  `;
};

const StyledLoginButton = styled(Button)`
  ${commonButtonStyles()};
`;

const StyledDemoButton = styled(Button)`
  ${commonButtonStyles()};

  ${maxBreakpointQuery.large`
    background: none;
    border: 1px solid ${standardColours.white};
  `}
`;

const Navigation = ({ displayNav, setDisplayNav, displaySearch }) => {
  const {
    allDatoCmsNavigation: { nodes },
    datoCmsHeader: { loginLink, demoLink },
  } = useStaticQuery(graphql`
    query NavigationQuery {
      allDatoCmsNavigation(
        filter: { root: { eq: true } }
        sort: { position: ASC }
      ) {
        nodes {
          link {
            text
            pageUrl {
              ...LinkFragment
            }
          }
          treeChildren {
            link {
              text
              pageUrl {
                ...LinkFragment
              }
            }
            position
          }
        }
      }
      datoCmsHeader {
        loginLink {
          text
          pageUrl {
            ...LinkFragment
          }
        }
        demoLink {
          text
          pageUrl {
            ...LinkFragment
          }
        }
      }
    }
  `);

  return (
    <StyledNavigation isSearchOpen={displaySearch}>
      <StyledToggle
        aria-label="Open / Close Navigation"
        displayNav={displayNav}
        onClick={() => setDisplayNav(!displayNav)}
      >
        <span />
        <span />
        <span />
      </StyledToggle>
      <StyledInner displayNav={displayNav}>
        <StyledList>
          {nodes.map(({ link: { text, pageUrl }, treeChildren }, id) => {
            const hasChildren = treeChildren && treeChildren.length > 0;

            return (
              <StyledItem key={id} hasChildren={hasChildren}>
                <StyledLink
                  to={pageUrl}
                  activeClassName="current-page"
                  hasChildren={hasChildren}
                >
                  {text}
                </StyledLink>
                {hasChildren && (
                  <StyledSubList>
                    {treeChildren
                      .sort((a, b) => a.position - b.position)
                      .map(({ link: { text, pageUrl }, id }) => (
                        <StyledSubItem key={id}>
                          <StyledSubLink
                            to={pageUrl}
                            activeClassName="current-page"
                          >
                            {text}
                          </StyledSubLink>
                        </StyledSubItem>
                      ))}
                  </StyledSubList>
                )}
              </StyledItem>
            );
          })}
        </StyledList>
        <StyledLoginButton to={loginLink.pageUrl}>
          {loginLink.text}
        </StyledLoginButton>
        <StyledDemoButton to={demoLink.pageUrl}>
          {demoLink.text}
        </StyledDemoButton>
      </StyledInner>
    </StyledNavigation>
  );
};

export default Navigation;
